// src/components/terms/TermsEditModal.tsx
import * as React from "react";
import dayjs, { Dayjs } from "dayjs";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import type { TermsItem, TermsCode } from "src/store/terms";

type Mode = "create" | "edit";

type Props = {
  open: boolean;
  mode: Mode;
  item: TermsItem | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: Partial<TermsItem> & { termsId?: number }) => Promise<void>;
};

function toDayjs(iso?: string): Dayjs | null {
  if (!iso) return null;
  const d = dayjs(iso);
  return d.isValid() ? d : null;
}
function toIso(d: Dayjs | null): string {
  return d ? d.toISOString() : "";
}

export default function TermsEditModal({
  open,
  mode,
  item,
  loading = false,
  onClose,
  onSubmit,
}: Props) {
  const isEdit = mode === "edit";

  const initial = React.useMemo<Partial<TermsItem>>(
    () =>
      item ?? {
        termsCode: "TERMS_OF_SERVICE" as TermsCode, // ✅ 기본값
        termsType: "SERVICE",
        termsStatus: "PUBLISHED",
        termsRequired: true,
        termsOrder: 0,
        applyStrDatetime: dayjs().toISOString(),
        applyEndDatetime: dayjs().toISOString(),
      },
    [item]
  );

  const [form, setForm] = React.useState<Partial<TermsItem>>(initial);

  React.useEffect(() => {
    if (!open) return;
    setForm(initial);
  }, [open, initial]);

  const set = <K extends keyof TermsItem>(k: K, v: TermsItem[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
  };

  const startValue = React.useMemo(() => toDayjs(form.applyStrDatetime), [form.applyStrDatetime]);
  const endValue = React.useMemo(() => toDayjs(form.applyEndDatetime), [form.applyEndDatetime]);

  const canSave =
    !!String(form.termsCode ?? "") && // ✅ 추가
    !!String(form.termsType ?? "") &&
    !!String(form.termsTtl ?? "").trim() &&
    !!String(form.termsCts ?? "").trim() &&
    !!String(form.termsStatus ?? "") &&
    !!String(form.applyStrDatetime ?? "") &&
    !!String(form.applyEndDatetime ?? "");

  const buildPayload = (): Partial<TermsItem> & { termsId?: number } => {
    const base: Partial<TermsItem> = {
      termsCode: String(form.termsCode ?? "") as TermsCode, // ✅ 추가
      termsType: String(form.termsType ?? ""),
      termsStatus: String(form.termsStatus ?? ""),
      termsTtl: String(form.termsTtl ?? "").trim(),
      termsCts: String(form.termsCts ?? "").trim(),
      termsOrder: Number(form.termsOrder ?? 0),
      termsRequired: !!form.termsRequired,
      applyStrDatetime: String(form.applyStrDatetime ?? ""),
      applyEndDatetime: String(form.applyEndDatetime ?? ""),
    };

    if (isEdit && item?.termsId != null) return { ...base, termsId: item.termsId };
    return base;
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 900 }}>
        {isEdit ? "이용약관 수정" : "이용약관 등록"}
      </DialogTitle>

      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              {/* ✅ termsCode */}
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="termsCode-label">termsCode</InputLabel>
                <Select
                  labelId="termsCode-label"
                  label="termsCode"
                  value={(form.termsCode ?? "") as any}
                  onChange={(e) => set("termsCode", String(e.target.value) as any)}
                  disabled={loading}
                >
                  <MenuItem value="TERMS_OF_SERVICE">TERMS_OF_SERVICE</MenuItem>
                  <MenuItem value="PRIVACY_POLICY">PRIVACY_POLICY</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="termsType-label">termsType</InputLabel>
                <Select
                  labelId="termsType-label"
                  label="termsType"
                  value={(form.termsType ?? "") as any}
                  onChange={(e) => set("termsType", String(e.target.value) as any)}
                  disabled={loading}
                >
                  <MenuItem value="SERVICE">SERVICE</MenuItem>
                  <MenuItem value="PRIVACY">PRIVACY</MenuItem>
                  <MenuItem value="MARKETING">MARKETING</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="termsStatus-label">termsStatus</InputLabel>
                <Select
                  labelId="termsStatus-label"
                  label="termsStatus"
                  value={(form.termsStatus ?? "") as any}
                  onChange={(e) => set("termsStatus", String(e.target.value) as any)}
                  disabled={loading}
                >
                  <MenuItem value="DRAFT">DRAFT(초안)</MenuItem>
                  <MenuItem value="PUBLISHED">PUBLISHED(발행)</MenuItem>
                  <MenuItem value="DEPRECATED">DEPRECATED(미사용)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="termsOrder"
                type="number"
                value={String(form.termsOrder ?? 0)}
                onChange={(e) => set("termsOrder", Number(e.target.value) as any)}
                sx={{ width: 140 }}
                disabled={loading}
              />
            </Stack>

            <TextField
              size="small"
              label="termsTtl"
              value={String(form.termsTtl ?? "")}
              onChange={(e) => set("termsTtl", e.target.value as any)}
              fullWidth
              disabled={loading}
            />

            <TextField
              size="small"
              label="termsCts"
              value={String(form.termsCts ?? "")}
              onChange={(e) => set("termsCts", e.target.value as any)}
              multiline
              minRows={8}
              disabled={loading}
              sx={{ "& textarea": { resize: "vertical" } }}
            />

            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.termsRequired}
                    onChange={(e) => set("termsRequired", e.target.checked as any)}
                    disabled={loading}
                  />
                }
                label="필수 약관(termsRequired)"
              />
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                * 필수 선택 여부
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2}>
              <DateTimePicker
                label="applyStrDatetime"
                value={startValue}
                onChange={(v) => set("applyStrDatetime", toIso(v) as any)}
                slotProps={{
                  textField: { size: "small", fullWidth: true, disabled: loading },
                  popper: { sx: { zIndex: 20000 } },
                }}
              />

              <DateTimePicker
                label="applyEndDatetime"
                value={endValue}
                onChange={(v) => set("applyEndDatetime", toIso(v) as any)}
                minDateTime={startValue ?? undefined}
                slotProps={{
                  textField: { size: "small", fullWidth: true, disabled: loading },
                  popper: { sx: { zIndex: 20000 } },
                }}
              />
            </Stack>
          </Stack>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={loading}>
          닫기
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(buildPayload())}
          disabled={!canSave || loading}
        >
          {loading ? "저장 중..." : "저장"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
